import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api-client'
import { queryKeys } from '@/lib/query-keys'
import type { IAdminCaseHistoryResponse } from '@/types/case-history'
import type { IAdminLoginHistoryResponse } from '@/types/login-history'
import type { IAdminPackHistoryResponse } from '@/types/pack-history'
import type { IBanActionResponse, IBanHistoryResponse } from '@/types/ban-history'
import type { CryptoAddressMap } from '@/types/payment'
import type { IUser, UserPermission, UserRole, UserSortField } from '@/types/user'

import { expectSuccess, type ApiEnvelope } from './helpers'

export interface UserListParams {
    page: number
    limit: number
    sortBy?: UserSortField | string
    sortOrder?: 'asc' | 'desc'
    role?: UserRole | string
    search?: string
}

interface UserListResponse extends ApiEnvelope {
    users: IUser[]
    count: number
}

export async function fetchUsers(params: Record<string, unknown>, signal?: AbortSignal) {
    const response = await api.get<UserListResponse>('/secure/admin/user', {
        params: params as never,
        signal
    })
    return expectSuccess(response, 'Could not load users.')
}

/**
 * There is no single-user endpoint: the list is queried with `limit=1` and the id in
 * `search`, exactly as the legacy `useAdminUser` did. Kept in one place so callers
 * are not reproducing that trick, and cached under a detail key so a user page and
 * the users list do not evict each other.
 *
 * Options rather than only a hook, so a route loader warms the very cache entry the
 * page then reads instead of fetching the same user twice.
 */
export function userQueryOptions(userId: string) {
    return queryOptions({
        queryKey: queryKeys.users.detail(userId),
        queryFn: async ({ signal }) => {
            const response = await fetchUsers({ page: 1, limit: 1, search: userId }, signal)
            const user = response.users[0]
            if (!user) throw new Error('User not found')
            return user
        }
    })
}

export function useUser(userId: string, options: { enabled?: boolean } = {}) {
    return useQuery({ ...userQueryOptions(userId), enabled: (options.enabled ?? true) && userId !== '' })
}

export function useUpdateUser(userId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        // The API takes a partial patch, and the UI sends one field per call, which
        // keeps a failed edit from reverting the fields that saved.
        mutationFn: async (data: Partial<IUser>) => {
            const response = await api.post<ApiEnvelope>('/secure/admin/user/update', { userId, data })
            return expectSuccess(response, 'Could not update the user.')
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) })
            void queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() })
        }
    })
}

export function useUpdateUserRole(userId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (role: UserRole) => {
            const response = await api.post<ApiEnvelope>('/secure/admin/user/update-role', { userId, role })
            return expectSuccess(response, 'Could not change the role.')
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) })
        }
    })
}

export function useUpdateUserPermissions(userId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        // The full set, not a delta: the endpoint replaces what it is given, so
        // sending an addition alone would revoke everything else.
        mutationFn: async (permissions: UserPermission[]) => {
            const response = await api.post<ApiEnvelope>('/secure/admin/user/update-permissions', {
                userId,
                permissions
            })
            return expectSuccess(response, 'Could not update permissions.')
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) })
        }
    })
}

export function loginHistoryQueryOptions(userId: string, params: { page: number; limit: number }) {
    return queryOptions({
        queryKey: queryKeys.users.logins(userId, params),
        queryFn: async ({ signal }) => {
            const response = await api.get<IAdminLoginHistoryResponse>(`/secure/admin/user/logins/${userId}`, {
                params,
                signal
            })
            return expectSuccess(response as IAdminLoginHistoryResponse & ApiEnvelope, 'Could not load login history.')
        }
    })
}

export function useLoginHistory(userId: string, params: { page: number; limit: number }) {
    return useQuery({ ...loginHistoryQueryOptions(userId, params), enabled: userId !== '' })
}

export function useCaseHistory(userId: string, params: { page: number; limit: number }) {
    return useQuery({
        queryKey: queryKeys.users.caseHistory(userId, params),
        enabled: userId !== '',
        queryFn: async ({ signal }) => {
            const response = await api.get<IAdminCaseHistoryResponse>(`/secure/admin/user/case-history/${userId}`, {
                params,
                signal
            })
            return expectSuccess(response as IAdminCaseHistoryResponse & ApiEnvelope, 'Could not load card openings.')
        }
    })
}

/**
 * Pack openings, the mirror of `useCaseHistory`. The route is
 * `GET /secure/admin/user/pack-history/:userId`, same `USER_VIEW` gate and same
 * `{page, limit}` query as case history, answering `{success, packs, count}`.
 */
export function usePackHistory(userId: string, params: { page: number; limit: number }) {
    return useQuery({
        queryKey: queryKeys.users.packHistory(userId, params),
        enabled: userId !== '',
        queryFn: async ({ signal }) => {
            const response = await api.get<IAdminPackHistoryResponse>(`/secure/admin/user/pack-history/${userId}`, {
                params,
                signal
            })
            return expectSuccess(response as IAdminPackHistoryResponse & ApiEnvelope, 'Could not load pack openings.')
        }
    })
}

export function useBanHistory(userId: string, params: { page: number; limit: number }, enabled = true) {
    return useQuery({
        queryKey: queryKeys.users.bans(userId, params),
        enabled: enabled && userId !== '',
        queryFn: async ({ signal }) => {
            const response = await api.get<IBanHistoryResponse>(`/secure/admin/user/bans/${userId}`, {
                params,
                signal
            })
            return expectSuccess(response, 'Could not load ban history.')
        }
    })
}

export interface BanInput {
    reason?: string
    /** Omit both for a permanent ban. The API requires them together. */
    duration?: number
    unit?: 'hours' | 'days'
}

export function useBanUser(userId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (input: BanInput) => {
            const reason = input.reason?.trim()
            const response = await api.post<IBanActionResponse>('/secure/admin/user/ban', {
                userId,
                ...(reason ? { reason } : {}),
                ...(input.duration !== undefined && input.unit ? { duration: input.duration, unit: input.unit } : {})
            })
            return expectSuccess(response, 'Could not ban the user.')
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['users', 'bans', userId] })
            void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) })
        }
    })
}

export function useUnbanUser(userId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (reason?: string) => {
            const trimmed = reason?.trim()
            const response = await api.post<IBanActionResponse>('/secure/admin/user/unban', {
                userId,
                ...(trimmed ? { reason: trimmed } : {})
            })
            return expectSuccess(response, 'Could not lift the ban.')
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['users', 'bans', userId] })
            void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) })
        }
    })
}

export function useCryptoAddresses(userId: string, enabled = true) {
    return useQuery({
        queryKey: queryKeys.users.cryptoAddresses(userId),
        enabled: enabled && userId !== '',
        queryFn: async ({ signal }) => {
            const response = await api.get<ApiEnvelope & { addresses: CryptoAddressMap }>(
                `/secure/admin/user/crypto-addresses/${userId}`,
                { signal }
            )
            // A user with no deposit addresses is normal, not an error worth surfacing.
            return response.success ? response.addresses : {}
        }
    })
}
